/**
 * F09 v2 (E7) — admin document list + viewer.
 *
 * Bytes are fetched ONLY through the authenticated proxy (`doc.contentPath`)
 * with the bearer token, into a blob object URL:
 *   - images  → `ImageLightbox`
 *   - PDFs    → an UNSANDBOXED same-origin blob `<iframe>` (Chrome refuses to
 *               render PDFs inside sandboxed frames)
 *   - legacy v1 HEIC (`renderMode: 'download'`) → a logged download button
 *
 * Every fetch writes one server-side access-log row, so each view re-fetches
 * and shows an "Access logged · you · {time}" strip. Object URLs are revoked
 * when a viewer closes and on unmount. A viewer without `verifications:review`
 * (support) sees "Restricted" and no viewer at all. A document the requester
 * has not submitted yet (`canView: false`, no round, not legacy) is never
 * served by the API, so a reviewer sees "Not submitted yet" instead.
 */
import { useEffect, useRef, useState } from 'react';
import { useFormat, useI18n } from '@bravemove/i18n/react';
import { Badge, Button, ImageLightbox, Modal } from '@bravemove/ui-web';
import type { AdminVerificationDocument } from '@bravemove/types';
import { adminVerificationApi } from '../../api/verification';
import { docKindLabel, requestStatusLabel } from './labels';
import { VerificationErrorBanner } from './VerificationBits';

type ViewMode = 'image' | 'pdf' | 'download';

/**
 * How long a download's object URL stays alive after the click. Some browsers
 * start reading the blob asynchronously, so revoking at once (or after ~1 s)
 * can fail a slow download start. Unmount still revokes it immediately.
 */
const DOWNLOAD_URL_TTL_MS = 40_000;

interface OpenViewer {
  readonly docId: string;
  readonly mode: 'image' | 'pdf';
  readonly url: string;
  readonly title: string;
}

function viewModeOf(doc: AdminVerificationDocument): ViewMode {
  if (doc.renderMode === 'download' || doc.mime === 'image/heic') return 'download';
  if (doc.mime === 'application/pdf') return 'pdf';
  return 'image';
}

function formatBytes(n: number): string {
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
}

export function DocumentList({
  documents,
  canReview,
  onViewed,
}: {
  readonly documents: readonly AdminVerificationDocument[];
  readonly canReview: boolean;
  /** Called after each successful (logged) view, e.g. to bump the access count. */
  readonly onViewed?: () => void;
}) {
  const { t } = useI18n();
  const { formatTime } = useFormat();
  const [viewer, setViewer] = useState<OpenViewer | null>(null);
  const [loadingDocId, setLoadingDocId] = useState<string | null>(null);
  const [viewedAt, setViewedAt] = useState<Readonly<Record<string, string>>>({});
  const [error, setError] = useState<unknown>(null);
  const urls = useRef<Set<string>>(new Set());
  const downloadTimers = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  // Revoke every object URL this list created (and drop pending download
  // timers) when it unmounts.
  useEffect(() => {
    const created = urls.current;
    const timers = downloadTimers.current;
    return () => {
      for (const timer of timers) clearTimeout(timer);
      timers.clear();
      for (const url of created) URL.revokeObjectURL(url);
      created.clear();
    };
  }, []);

  const release = (url: string) => {
    URL.revokeObjectURL(url);
    urls.current.delete(url);
  };

  const closeViewer = () => {
    if (viewer) release(viewer.url);
    setViewer(null);
  };

  const open = async (doc: AdminVerificationDocument) => {
    const mode = viewModeOf(doc);
    setLoadingDocId(doc.docId);
    setError(null);
    try {
      const { blob } = await adminVerificationApi.fetchDocumentContent(doc.contentPath);
      const url = URL.createObjectURL(blob);
      urls.current.add(url);
      setViewedAt((prev) => ({ ...prev, [doc.docId]: new Date().toISOString() }));
      onViewed?.();
      if (mode === 'download') {
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.originalFilename ?? `${doc.docId}.heic`;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
        const timer = setTimeout(() => {
          downloadTimers.current.delete(timer);
          release(url);
        }, DOWNLOAD_URL_TTL_MS);
        downloadTimers.current.add(timer);
        return;
      }
      if (viewer) release(viewer.url);
      setViewer({
        docId: doc.docId,
        mode,
        url,
        title: t('admin:verification.documents.viewerTitle', { kind: docKindLabel(t, doc.kind) }),
      });
    } catch (err) {
      setError(err);
    } finally {
      setLoadingDocId(null);
    }
  };

  if (documents.length === 0) {
    return <p className="text-[13px] text-ink-3">{t('admin:verification.documents.empty')}</p>;
  }

  return (
    <div className="space-y-3" data-testid="verification-documents">
      {error ? <VerificationErrorBanner error={error} testId="document-error" /> : null}
      <ul className="space-y-2">
        {documents.map((doc) => {
          const mode = viewModeOf(doc);
          const viewable = canReview && doc.canView;
          // A reviewer who still can't open it: the requester never submitted it.
          const notSubmitted = canReview && !doc.canView && doc.round === null && !doc.legacyV1;
          const at = viewedAt[doc.docId];
          return (
            <li
              key={doc.docId}
              className="rounded-lg border border-hairline bg-surface px-3 py-2.5"
              data-testid={`document-${doc.docId}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0 space-y-0.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-ink">{docKindLabel(t, doc.kind)}</span>
                    {doc.round !== null ? (
                      <Badge tone="neutral" size="sm">
                        {t('admin:verification.documents.round', { round: doc.round })}
                      </Badge>
                    ) : null}
                    {mode === 'download' ? (
                      <Badge tone="warning" size="sm">
                        {t('admin:verification.documents.legacyHeic')}
                      </Badge>
                    ) : null}
                  </div>
                  <div className="font-mono text-[11px] text-ink-3">
                    {[doc.originalFilename, doc.mime, formatBytes(doc.sizeBytes)]
                      .filter((x): x is string => !!x)
                      .join(' · ')}
                  </div>
                </div>
                {viewable ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    icon={mode === 'download' ? 'download' : 'eye'}
                    loading={loadingDocId === doc.docId}
                    data-testid={`document-${mode === 'download' ? 'download' : 'view'}-${doc.docId}`}
                    onClick={() => void open(doc)}
                  >
                    {mode === 'download'
                      ? t('admin:verification.documents.download')
                      : t('admin:verification.documents.view')}
                  </Button>
                ) : notSubmitted ? (
                  <span
                    className="inline-flex items-center gap-1.5"
                    title={t('admin:verification.documents.notSubmittedHint')}
                    data-testid={`document-not-submitted-${doc.docId}`}
                  >
                    <Badge tone="neutral" size="sm">
                      {t('admin:verification.documents.notSubmitted')}
                    </Badge>
                  </span>
                ) : (
                  <span
                    className="inline-flex items-center gap-1.5"
                    title={t('admin:verification.documents.restrictedHint')}
                    data-testid={`document-restricted-${doc.docId}`}
                  >
                    <Badge tone="neutral" size="sm">
                      {t('admin:verification.documents.restricted')}
                    </Badge>
                  </span>
                )}
              </div>
              {doc.duplicateMatches.length > 0 ? (
                <ul className="mt-2 space-y-1">
                  {doc.duplicateMatches.map((m) => (
                    <li
                      key={`${m.requestId}:${m.docId ?? ''}`}
                      className="text-[12px]"
                      data-same-subject={m.sameSubject ? 'true' : 'false'}
                    >
                      <Badge tone={m.sameSubject ? 'neutral' : 'danger'} size="sm">
                        {m.sameSubject
                          ? t('admin:verification.documents.duplicateSameSubject')
                          : t('admin:verification.panels.otherSubject')}
                      </Badge>{' '}
                      <span className="text-ink-2">
                        {t('admin:verification.documents.duplicateOf', {
                          entity: m.entityName ?? m.entityId,
                          status: requestStatusLabel(t, m.requestStatus),
                        })}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : null}
              {at ? (
                <p
                  className="mt-2 font-mono text-[11px] text-ink-3"
                  data-testid={`document-access-${doc.docId}`}
                >
                  {t('admin:verification.documents.accessStrip', {
                    time: formatTime(new Date(at)),
                  })}
                </p>
              ) : null}
            </li>
          );
        })}
      </ul>

      {viewer?.mode === 'image' ? (
        <ImageLightbox src={viewer.url} alt={viewer.title} open onClose={closeViewer} />
      ) : null}
      {viewer?.mode === 'pdf' ? (
        <Modal open onClose={closeViewer} title={viewer.title} size="lg">
          {/* Deliberately NOT sandboxed: Chrome will not render a PDF in a
              sandboxed frame. The blob URL is same-origin and the view is
              already logged server-side (E7). */}
          <iframe
            src={viewer.url}
            title={viewer.title}
            className="h-[70vh] w-full rounded-md border border-hairline bg-surface"
            data-testid="document-pdf-frame"
          />
        </Modal>
      ) : null}
    </div>
  );
}
